import { useEffect, useMemo } from "react";
import { queryFonts } from "@/Services";

// Curated Google Fonts already referenced by dashboard-site wordmarks (see
// config/audience_network_packages.php). These were never actually linked
// anywhere, so typed values like "Archivo Black" silently fell back to a
// system font — loaded here once, alongside any custom uploads.
const BUILT_IN_FONTS = [
    { value: "Inter", label: "Inter" },
    { value: "Archivo", label: "Archivo" },
    { value: "Archivo Black", label: "Archivo Black" },
    { value: "Playfair Display", label: "Playfair Display" },
];

const GOOGLE_FONTS_HREF = "https://fonts.googleapis.com/css2?" + BUILT_IN_FONTS
    .map((font) => `family=${encodeURIComponent(font.value)}:wght@400;700;800;900`)
    .join("&") + "&display=swap";

const FORMAT_LABELS = { ttf: "truetype", otf: "opentype", woff: "woff", woff2: "woff2" };

let googleFontsLinked = false;
const injectedCustomFontKeys = new Set();

const ensureGoogleFontsLinked = () => {
    if (googleFontsLinked || typeof document === "undefined") return;
    googleFontsLinked = true;

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = GOOGLE_FONTS_HREF;
    document.head.appendChild(link);
};

const ensureCustomFontFace = (font) => {
    if (!font?.name || !font?.url || typeof document === "undefined") return;

    const key = `${font.name}::${font.url}`;
    if (injectedCustomFontKeys.has(key)) return;
    injectedCustomFontKeys.add(key);

    const format = FORMAT_LABELS[font.format] || font.format;
    const style = document.createElement("style");
    style.setAttribute("data-custom-font", font.name);
    style.textContent = `@font-face { font-family: "${font.name.replace(/"/g, '\\"')}"; src: url("${font.url}") format("${format}"); font-display: swap; }`;
    document.head.appendChild(style);
};

// Fetches custom fonts, injects their @font-face rules and the curated
// Google Fonts stylesheet into <head>, and returns a combined Select-ready
// options list. Safe to call from multiple components at once.
export const useDashboardFonts = () => {
    const queryCustomFonts = queryFonts();
    const customFonts = queryCustomFonts?.data || [];

    useEffect(() => {
        ensureGoogleFontsLinked();
    }, []);

    useEffect(() => {
        customFonts.forEach(ensureCustomFontFace);
    }, [customFonts]);

    const fontOptions = useMemo(() => ([
        ...BUILT_IN_FONTS,
        ...customFonts.map((font) => ({
            value: font.name,
            label: `${font.name} (custom)`,
            isCustom: true,
        })),
    ]), [customFonts]);

    return {
        fontOptions,
        customFonts,
        isLoading: Boolean(queryCustomFonts?.isLoading),
    };
};
